/**
 * 메뉴 역할세트 React Hooks
 *
 * 목록 조회(useApi 패턴) + 생성/수정/삭제 mutation(useApiMutation 패턴).
 */

import { menuRoleSetClient } from '@/lib/menu-role-set-client';
import type { MenuRoleSet, MenuRoleSetSaveRequest } from '@/types/menu-role-set';
import { useCallback } from 'react';
import { useApi, useApiMutation } from './useApi';

export const useMenuRoleSetList = () => {
  return useApi<MenuRoleSet[]>(() => menuRoleSetClient.list(), []);
};

export const useCreateMenuRoleSet = () => {
  const { mutate, loading, error } = useApiMutation<MenuRoleSet, MenuRoleSetSaveRequest>();

  const createMenuRoleSet = useCallback(
    async (request: MenuRoleSetSaveRequest) => {
      return await mutate(req => menuRoleSetClient.create(req), request);
    },
    [mutate]
  );

  return { createMenuRoleSet, loading, error };
};

export const useUpdateMenuRoleSet = () => {
  const { mutate, loading, error } = useApiMutation<
    MenuRoleSet,
    { setId: number; request: MenuRoleSetSaveRequest }
  >();

  const updateMenuRoleSet = useCallback(
    async (setId: number, request: MenuRoleSetSaveRequest) => {
      return await mutate(
        ({ setId: id, request: req }) => menuRoleSetClient.update(id, req),
        { setId, request }
      );
    },
    [mutate]
  );

  return { updateMenuRoleSet, loading, error };
};

export const useRemoveMenuRoleSet = () => {
  const { mutate, loading, error } = useApiMutation<void, number>();

  const removeMenuRoleSet = useCallback(
    async (setId: number) => {
      return await mutate(id => menuRoleSetClient.remove(id), setId);
    },
    [mutate]
  );

  return { removeMenuRoleSet, loading, error };
};
